import React, { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PrinterStatusIndicator from './PrinterStatusIndicator';
import logo from '../assets/logo.png';

const BarraLateral = ({ isMobileOpen, onCloseMobile, isCollapsed, toggleSidebar }) => {
    const location = useLocation();
    const { user, logout } = useContext(AuthContext);

    const isActive = (path) => {
        if (path === '/') {
            return location.pathname === '/' ? 'active' : '';
        }
        return location.pathname === path || location.pathname.startsWith(path) ? 'active' : '';
    };

    const handleNavClick = () => {
        if (onCloseMobile) {
            onCloseMobile();
        }
    };

    const menuItems = [
        { path: '/', icon: 'bi-house-door-fill', label: 'Inicio' },
        { path: '/fast-food/pos', icon: 'bi-cup-hot-fill', label: 'Cafetería (POS)' },
        { path: '/fast-food/loncheras', icon: 'bi-backpack2-fill', label: 'Loncheras Escolares' },
        { path: '/fast-food/escuelas', icon: 'bi-buildings-fill', label: 'Escuelas (Tulcán)' },
        { path: '/fast-food/cafe-grano', icon: 'bi-bag-check-fill', label: 'Café en Grano / Peso' },
        { path: '/fast-food/orders', icon: 'bi-receipt', label: 'Historial de Órdenes' },
        { path: '/fast-food/inventory', icon: 'bi-cup-straw', label: 'Catálogo de Bebidas' },
        { path: '/fast-food/shift', icon: 'bi-cash-coin', label: 'Caja Principal' },
        { path: '/fast-food/feria-shift', icon: 'bi-shop', label: 'Caja de Ferias' },
        { path: '/fast-food/bodega', icon: 'bi-archive-fill', label: 'Bodega de Insumos' },
        { path: '/fast-food/gastos', icon: 'bi-wallet2', label: 'Gastos' },
        { path: '/fast-food/facturacion-sri', icon: 'bi-file-earmark-check-fill', label: 'Facturación SRI' },
        { path: '/fast-food/reports', icon: 'bi-graph-up-arrow', label: 'Reportes & Cierres' },
        { path: '/fast-food/ganancias', icon: 'bi-piggy-bank', label: 'Costos y Ganancias' },
        { path: '/fast-food/periodic-reports', icon: 'bi-calendar-week', label: 'Reportes Semanales' },
        { path: '/fast-food/printers', icon: 'bi-printer-fill', label: 'Impresoras Térmicas' },
        { path: '/fast-food/secuencia', icon: 'bi-123', label: 'Configuración Turnos' },
        { path: '/fast-food/customers', icon: 'bi-person-badge', label: 'Clientes' },
        { path: '/fast-food/workers', icon: 'bi-person-vcard-fill', label: 'Trabajadores' },
        { path: '/cobranzas', icon: 'bi-shield-lock-fill', label: 'Gestor de Cobranzas' },
        { path: '/users', icon: 'bi-people-fill', label: 'Usuarios' },
    ];

    const roleName = user?.role_details?.name;
    const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
    const isGestorCobranzas = roleName === 'GESTOR_COBRANZAS';
    const isAdminFastFood = roleName === 'ADMIN_FAST_FOOD';
    const isCook = roleName === 'COOK' || user?.role === 'COOK' || user?.role_details?.description?.toLowerCase()?.includes('cocinero');

    const filteredMenuItems = menuItems.filter(item => {
        // Si es Gestor de Cobranzas, ve su panel de Cobranzas
        if (isGestorCobranzas) {
            return item.path === '/cobranzas';
        }

        // El Gestor de Cobranzas es exclusivo de su rol: nadie más lo ve (ni el administrador)
        if (item.path === '/cobranzas') return false;

        // Si es Super Admin, ve todo el sistema
        if (isSuperAdmin) return true;

        // Otros roles no administrativos no ven cobranzas ni usuarios
        if (item.path === '/cobranzas' || item.path === '/users') {
            return false;
        }

        if (isAdminFastFood && item.path.startsWith('/fast-food')) return true;

        // Si es Cocinero / Barista: ve módulos de operación
        if (isCook) {
            const cookPaths = [
                '/fast-food/pos',
                '/fast-food/loncheras',
                '/fast-food/escuelas',
                '/fast-food/cafe-grano',
                '/fast-food/orders',
                '/fast-food/inventory',
                '/fast-food/shift',
                '/fast-food/feria-shift'
            ];
            return cookPaths.includes(item.path);
        }

        // Para empleados normales (POS, Loncheras, Escuelas, Café en grano, Órdenes, Bebidas y Caja)
        const employeePaths = [
            '/fast-food/pos',
            '/fast-food/loncheras',
            '/fast-food/escuelas',
            '/fast-food/cafe-grano',
            '/fast-food/orders',
            '/fast-food/inventory',
            '/fast-food/shift'
        ];
        return employeePaths.includes(item.path);
    });

    return (
        <>
            {/* Overlay Oscuro para Móvil y Tablet */}
            <div
                className={`sidebar-backdrop ${isMobileOpen ? 'active' : ''}`}
                onClick={onCloseMobile}
                aria-hidden="true"
            />

            <aside className={`app-sidebar ${isCollapsed ? 'collapsed' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
                {/* Header con Logo, Título y Botón Toggle */}
                <div className="app-sidebar-header">
                    <div className="sidebar-brand-row">
                        <div className="sidebar-logo-wrap">
                            <img
                                src={logo}
                                alt="Sophia's Coffee"
                                className="sidebar-logo-img"
                            />
                        </div>

                        {/* Botón Collapse (Solo Desktop) */}
                        <button
                            type="button"
                            className="sidebar-collapse-btn desktop-only"
                            onClick={toggleSidebar}
                            title={isCollapsed ? 'Expandir menú lateral' : 'Contraer menú lateral'}
                        >
                            <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                        </button>

                        {/* Botón Cerrar (Solo Móvil/Tablet) */}
                        <button
                            type="button"
                            className="sidebar-close-btn mobile-only"
                            onClick={onCloseMobile}
                            title="Cerrar Menú"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    </div>

                    {/* Info del Usuario Logueado */}
                    <div className="sidebar-user-card">
                        <div className="sidebar-user-avatar">
                            {user?.username?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="sidebar-user-info">
                            <div className="sidebar-user-name" title={user?.username || 'Usuario'}>
                                {user?.username || 'Usuario'}
                            </div>
                            <div className="sidebar-user-role" title={user?.role || 'Administrador'}>
                                {user?.role || 'Administrador'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Lista de Navegación con Scroll */}
                <ul className="sidebar-nav-list">
                    {filteredMenuItems.map((item) => {
                        const active = isActive(item.path);
                        return (
                            <li key={item.path} className="sidebar-nav-item">
                                <Link
                                    to={item.path}
                                    title={isCollapsed ? item.label : ''}
                                    className={`sidebar-nav-link ${active ? 'active' : ''}`}
                                    onClick={handleNavClick}
                                >
                                    <i className={`bi ${item.icon} sidebar-nav-icon`}></i>
                                    <span className="sidebar-nav-text">{item.label}</span>
                                    {active && <span className="sidebar-active-indicator" />}
                                </Link>
                            </li>
                        );
                    })}
                </ul>

                {/* Footer con Estado de Impresora y Cerrar Sesión */}
                <div className="sidebar-footer-wrap">
                    {!isCollapsed && (
                        <div className="sidebar-printer-status">
                            <PrinterStatusIndicator />
                        </div>
                    )}

                    <button
                        type="button"
                        className="sidebar-logout-btn"
                        onClick={logout}
                        title="Cerrar Sesión"
                    >
                        <i className="bi bi-box-arrow-right"></i>
                        <span className="sidebar-nav-text">Cerrar Sesión</span>
                    </button>

                    <div className="sidebar-version-tag">
                        Sophia's Coffee v1.2.0 • 2026
                    </div>
                </div>
            </aside>
        </>
    );
};

export default BarraLateral;